/*
 * COPYRIGHT © 2026 SANITÀNEXUS WHITE-LABEL. ALL RIGHTS RESERVED.
 * AUTHOR AND SOLE DEVELOPER: MARCO PANZETTI (C.F. PNZMRC89M14F251C)
 * Protected under Italian Copyright Law (Legge 633/1941) and International Treaties.
 * Unauthorized copying, reverse engineering, or distribution of this software architecture,
 * UI/UX layouts, local-first logic, or system prompt configurations is strictly prohibited.
 */
import React, { useCallback, useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import api, { describeError } from '@/lib/api';
import { Card } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { ShieldCheck, Users, FileText, Flag, MessageSquare, AlertTriangle, Check, Trash2, Loader2 } from 'lucide-react';
import { Tabs, TabsList, TabsTrigger } from '@/components/ui/tabs';
import AIStatsCard from '@/components/AIStatsCard';
import AdminAnalyticsCard from '@/components/AdminAnalyticsCard';
import AdminOnboardingPanel from '@/components/AdminOnboardingPanel';
import AdminIseeVerificationPanel from '@/components/admin/AdminIseeVerificationPanel';
import { timeAgo } from '@/lib/utils-cat';
import { toast } from 'sonner';

const StatCard = ({ Icon, label, value, color = 'var(--wl-primary)' }) => (
  <Card className="p-4 rounded-2xl bg-card flex items-center gap-3" data-testid={`admin-stat-${label}`}>
    <div className="h-10 w-10 rounded-xl flex items-center justify-center" style={{ backgroundColor: `${color}18`, color }}>
      <Icon className="h-5 w-5" />
    </div>
    <div>
      <div className="text-2xl font-display font-semibold">{value ?? '-'}</div>
      <div className="text-xs text-muted-foreground">{label}</div>
    </div>
  </Card>
);

const AdminPage = () => {
  const { t } = useTranslation();
  const [stats, setStats] = useState(null);
  const [reports, setReports] = useState([]);
  const [tab, setTab] = useState('pending');
  const [loading, setLoading] = useState(true);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const [s, r] = await Promise.all([
        api.get('/admin/stats'),
        api.get('/admin/reports', { params: { status: tab === 'all' ? undefined : tab } }),
      ]);
      setStats(s.data);
      setReports(r.data);
    } catch { toast.error(t('errorOccurred')); }
    finally { setLoading(false); }
  }, [tab, t]);

  useEffect(() => { load(); }, [load]);

  const [resolving, setResolving] = useState(null);
  const resolve = async (id, action) => {
    if (resolving) return; // anti doppio click
    setResolving(id);
    try {
      await api.post(`/admin/reports/${id}/resolve`, { action });
      toast.success(t('save'));
      await load();
    } catch (e) { toast.error(describeError(e, t('errorOccurred'))); } finally { setResolving(null); }
  };

  return (
    <div className="space-y-4">
      <div className="flex items-center gap-2">
        <ShieldCheck className="h-6 w-6 text-primary" />
        <h1 className="font-display text-2xl font-semibold tracking-tight">{t('adminPanel')}</h1>
      </div>

      <div>
        <div className="font-display font-semibold text-sm text-muted-foreground uppercase tracking-wider mb-2">{t('stats')}</div>
        <div className="grid grid-cols-2 md:grid-cols-5 gap-3">
          <StatCard Icon={Users} label={t('totalUsers')} value={stats?.users} />
          <StatCard Icon={FileText} label={t('totalPosts')} value={stats?.posts} color="#1D4ED8" />
          <StatCard Icon={Flag} label={t('initiatives')} value={stats?.initiatives} color="#B45309" />
          <StatCard Icon={MessageSquare} label={t('totalMessages')} value={stats?.messages} color="#7C3AED" />
          <StatCard Icon={AlertTriangle} label={t('pendingReports')} value={stats?.pending_reports} color="#B91C1C" />
        </div>
      </div>

      <div>
        <div className="font-display font-semibold text-sm text-muted-foreground uppercase tracking-wider mb-2">{t('aiAdminStatsTitle')}</div>
        <AIStatsCard endpoint="/admin/ai-stats" titleKey="aiAdminStatsTitle" showTopUsers />
      </div>

      <AdminAnalyticsCard />

      {/* Registry verification queue — Onboarding Tier 2 admin review */}
      <div className="pt-2">
        <AdminOnboardingPanel />
      </div>

      {/* Sprint 9F — ISEE Verification hook */}
      <div className="pt-2">
        <AdminIseeVerificationPanel />
      </div>

      <div>
        <div className="font-display font-semibold text-sm text-muted-foreground uppercase tracking-wider mb-2">{t('reports')}</div>
        <Tabs value={tab} onValueChange={setTab}>
          <TabsList className="bg-secondary/60 rounded-full p-1">
            <TabsTrigger value="pending" className="rounded-full text-xs" data-testid="admin-reports-tab-pending">{t('pending')}</TabsTrigger>
            <TabsTrigger value="resolved" className="rounded-full text-xs" data-testid="admin-reports-tab-resolved">{t('resolved')}</TabsTrigger>
            <TabsTrigger value="all" className="rounded-full text-xs" data-testid="admin-reports-tab-all">{t('all')}</TabsTrigger>
          </TabsList>
        </Tabs>
        <div className="mt-3 space-y-2">
          {loading && <div className="text-sm text-muted-foreground">{t('loading')}</div>}
          {!loading && reports.length === 0 && <div className="text-sm text-muted-foreground">{t('noResults')}</div>}
          {reports.map(r => (
            <Card key={r.id} className="p-3 rounded-2xl bg-card" data-testid="admin-report-row">
              <div className="flex items-start justify-between gap-2">
                <div className="flex-1 min-w-0">
                  <div className="flex items-center gap-2 text-xs">
                    <span className="font-semibold uppercase">{r.target_type}</span>
                    <span className={`px-2 py-0.5 rounded-full text-[10px] ${r.status === 'pending' ? 'bg-yellow-100 text-yellow-800' : 'bg-green-100 text-green-800'}`}>{r.status}</span>
                    <span className="text-muted-foreground">{timeAgo(r.created_at, t)}</span>
                  </div>
                  <div className="text-sm mt-1"><b>{t('reportReason')}:</b> {r.reason}</div>
                  {r.target_preview && <div className="text-xs text-muted-foreground mt-1 line-clamp-2">“{r.target_preview}”</div>}
                  <div className="text-[11px] text-muted-foreground mt-1">{t('reportedBy')}: {r.reporter?.full_name || '?'}</div>
                </div>
                {r.status === 'pending' && (
                  <div className="flex flex-col gap-1">
                    <Button size="sm" variant="secondary" disabled={!!resolving} onClick={() => resolve(r.id, 'dismiss')} data-testid="admin-report-dismiss">{resolving === r.id ? <Loader2 className="h-3.5 w-3.5 mr-1 animate-spin" /> : <Check className="h-3.5 w-3.5 mr-1" />}{t('dismiss')}</Button>
                    <Button size="sm" variant="destructive" disabled={!!resolving} onClick={() => resolve(r.id, 'delete_target')} data-testid="admin-report-delete">{resolving === r.id ? <Loader2 className="h-3.5 w-3.5 mr-1 animate-spin" /> : <Trash2 className="h-3.5 w-3.5 mr-1" />}{t('deleteTarget')}</Button>
                  </div>
                )}
              </div>
            </Card>
          ))}
        </div>
      </div>
    </div>
  );
};

export default AdminPage;
